import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Divider,
  Box,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Close as CloseIcon } from "../../../../components/icons";
import Input from "../../../../components/Input";
import ButtonComp from "../../../../components/Button";
import { useVehicles } from "../../../../hooks/useVehicles";
import { canDriverUseVehicle } from "../../../../utils/fleetCompatibility";

type AssignDriverPayload = {
  vehicleID: string;
  caseReference: string;
};

const initialFormData: AssignDriverPayload = {
  vehicleID: "",
  caseReference: "",
};

const AssignDriverPopup = ({ open, onClose, handleAssign, selectedDriver, initialCaseReference = "", assignmentPurpose = "", requestReference = "", allocationRole = "", allocationSequence = null }: any) => {
  const { vehicles } = useVehicles({ availableOnly: true });
  const [formData, setFormData] = useState(initialFormData);
  const expectedVehicleType = allocationRole === "HEARSE"
    ? "HEARSE"
    : allocationRole === "FAMILY_CAR"
      ? "FAMILY_CAR"
      : allocationRole === "WAREHOUSE_CARGO"
        ? "UTILITY_VEHICLE"
        : "";
  const compatibleVehicles = vehicles.filter((vehicle: any) =>
    (!expectedVehicleType || vehicle.vehicleType === expectedVehicleType)
      && selectedDriver && canDriverUseVehicle(selectedDriver, vehicle));

  useEffect(() => {
    if (open) {
      setFormData((current) => ({ ...current, caseReference: initialCaseReference || current.caseReference }));
    } else {
      setFormData(initialFormData);
    }
  }, [open, initialCaseReference]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby=""
      aria-describedby=""
      slotProps={{
        paper: {
          sx: {
            overflow: "hidden",
            width: { xs: "95%", sm: 800 },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "primary.main",
          fontWeight: 600,
          fontSize: "25px",
        }}
      >
        Assign Driver
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ bgcolor: "primary.main", mx: 3 }} />

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Grid container spacing={2}>
          {(assignmentPurpose || requestReference) && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#F7F9FC", border: "1px solid #E1E6EF" }}>
                <Box fontWeight={800}>{assignmentPurpose === "WAREHOUSE_TRANSPORT" ? "Warehouse Material Transport" : "Funeral Service Transport"}</Box>
                <Box fontSize={13} color="text.secondary">Request: {requestReference || "Linked case request"}</Box>
                {allocationRole && (
                  <Box fontSize={13} fontWeight={800} color="primary.main" mt={0.5}>
                    Vehicle slot: {String(allocationRole).replaceAll("_", " ")} {allocationSequence || 1}
                  </Box>
                )}
              </Box>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Input
              select
              name="vehicleId"
              label="Assign Vehicle"
              value={formData.vehicleID}
              onChange={(e: any) =>
                setFormData({ ...formData, vehicleID: e.target.value })
              }
            >
              {compatibleVehicles.map((vehicle: any) => (
                <MenuItem key={vehicle.vehicleID} value={vehicle.vehicleID}>
                  {vehicle.manufacturerName} {vehicle.modelName} · {String(vehicle.vehicleType || "Vehicle").replaceAll("_", " ")} · {String(vehicle.requiredLicenseCode || "No licence restriction").replaceAll("_", " ")}
                </MenuItem>
              ))}
            </Input>
            <Box fontSize={12} color="text.secondary" mt={0.5}>
              {compatibleVehicles.length
                ? "Showing available vehicles compatible with this driver's licence and PDP."
                : "No compatible vehicles are currently available."}
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Input
              fullWidth
              label="Case Reference"
              variant="outlined"
              value={formData.caseReference}
              onChange={(e: any) =>
                setFormData({ ...formData, caseReference: e.target.value })
              }
            />
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="center" gap={3} mt={2}>
          <ButtonComp
            variant="outlined"
            width={140}
            height={45}
            onClick={onClose}
          >
            Cancel
          </ButtonComp>

          <ButtonComp
            variant="contained"
            width={140}
            height={45}
            onClick={() => handleAssign(formData)}
          >
            Assign
          </ButtonComp>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AssignDriverPopup;
